package com.dotdrives.web.pages.donations

import com.dotdrives.model.AttentionItem
import com.dotdrives.model.AttentionKind
import com.dotdrives.model.Donation
import kotlin.math.round
import kotlin.time.Instant

fun donationInitials(displayName: String): String =
    displayName.split(' ', '&')
        .filter(String::isNotBlank)
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }

/** Full, comma-grouped, signed money — "$25,000", "-$500", "$12.50". Null renders "—"
 * (RLS-hidden). `amount` is a `numeric(12,2)` — rounds to the nearest cent, never the
 * nearest dollar, so a $12.50 gift never silently displays as $12 (and the ledger's
 * displayed rows always sum to its displayed total). Cents are omitted only when they are
 * exactly zero. */
fun formatMoneyFull(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val totalCents = round(amount * 100).toLong()
    val sign = if (totalCents < 0) "-" else ""
    val absCents = kotlin.math.abs(totalCents)
    val whole = absCents / 100
    val cents = absCents % 100
    val wholePart = "$sign$${groupThousands(whole)}"
    return if (cents == 0L) wholePart else "$wholePart.${cents.toString().padStart(2, '0')}"
}

/** Abbreviated money for header stat tiles — "$8K", "$742K", "$1.2M". Null renders "—". */
fun formatMoneyCompact(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val abs = kotlin.math.abs(amount)
    val sign = if (amount < 0) "-" else ""
    return when {
        abs >= 1_000_000 -> "$sign$${trimDecimal(abs / 1_000_000)}M"
        abs >= 1_000 -> "$sign$${trimDecimal(abs / 1_000)}K"
        else -> "$sign$${abs.toLong()}"
    }
}

private fun trimDecimal(value: Double): String {
    val rounded = round(value * 10) / 10
    val asLong = rounded.toLong()
    return if (rounded == asLong.toDouble()) asLong.toString() else rounded.toString()
}

private fun groupThousands(value: Long): String =
    value.toString().reversed().chunked(3).joinToString(",").reversed()

private const val MONEY_HIDDEN = "—"

private val MONTHS = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC")

/** "AUG 31" — derived from the instant's own UTC ISO string, no date-library dependency. */
fun dateChip(instant: Instant): String {
    val iso = instant.toString()
    val month = iso.substring(5, 7).toInt()
    val day = iso.substring(8, 10).toInt()
    return "${MONTHS[month - 1]} $day"
}

/** "AUG" — the month-start header label ("AUG TOTAL"), same source as [dateChip]. */
fun monthAbbrev(instant: Instant): String = MONTHS[instant.toString().substring(5, 7).toInt() - 1]

fun sourceLabel(source: String): String = when (source) {
    "manual" -> "Manual"
    "form" -> "Form"
    "subscription" -> "Subscription"
    "everyware" -> "Everyware"
    else -> source.replaceFirstChar(Char::uppercase)
}

enum class QboStatus { Synced, Error, Queued, Processing, Reconciliation, Disconnected, NotQueued, Unknown }

/** Read directly off `qbo_synced_at`/`qbo_sync_error` — no status this client can't see. */
fun qboStatus(donation: Donation): QboStatus = when (donation.qboWorkflowStatus) {
    "accepted" -> QboStatus.Synced
    "failed" -> QboStatus.Error
    "queued" -> QboStatus.Queued
    "processing" -> QboStatus.Processing
    "reconciliation_required" -> QboStatus.Reconciliation
    "disconnected" -> QboStatus.Disconnected
    "not_queued" -> QboStatus.NotQueued
    else -> when { donation.qboSyncedAt != null -> QboStatus.Synced; donation.qboSyncError != null -> QboStatus.Error; else -> QboStatus.Unknown }
}
fun qboStatusLabel(status: QboStatus): String = when (status) {
    QboStatus.Synced -> "✓ Synced"
    QboStatus.Error -> "⚠ Failed"
    QboStatus.Queued -> "Queued"
    QboStatus.Processing -> "Processing"
    QboStatus.Reconciliation -> "Needs review"
    QboStatus.Disconnected -> "Not connected"
    QboStatus.NotQueued -> "Not queued"
    QboStatus.Unknown -> "Status unavailable"
}

enum class LedgerEmptyReason { NoDataYet, NoResultsForFilters }

/** Distinguishes "this org has never recorded a gift" from "these filters match nothing" —
 * the two empty states the design's own empty frame and an over-filtered ledger must not
 * be confused with each other. */
fun ledgerEmptyReason(hasActiveFilters: Boolean): LedgerEmptyReason =
    if (hasActiveFilters) LedgerEmptyReason.NoResultsForFilters else LedgerEmptyReason.NoDataYet

data class AttentionCopy(
    val icon: String,
    val title: String,
    val detail: String,
)

/** Reproduces the design's queue copy shape, but every value is read off real data — no
 * fabricated wire amounts or ages. `today` drives the "unpaid N days" figure the design
 * leads with (e.g. "Pledge unpaid 30 days"). */
fun attentionCopy(item: AttentionItem, today: Instant): AttentionCopy = when (item.kind) {
    AttentionKind.QboSyncError -> {
        val donation = requireNotNull(item.donation)
        AttentionCopy(
            icon = "⚠️",
            title = "QuickBooks can't sync ${donation.giverDisplayName}'s gift",
            detail = "${formatMoneyFull(donation.amount)} on ${dateChip(donation.occurredAt)} is unsynced — ${donation.qboSyncError}",
        )
    }
    AttentionKind.UnpaidPledge -> {
        val pledge = requireNotNull(item.pledge)
        val daysUnpaid = pledge.expectedBy?.let { expectedBy -> instantToDate(today).toEpochDays() - expectedBy.toEpochDays() }
        AttentionCopy(
            icon = "🧾",
            title = "Pledge unpaid " +
                (daysUnpaid?.let { "$it day${if (it == 1L) "" else "s"}" } ?: "past due") +
                " — ${pledge.giverDisplayName}",
            detail = buildString {
                append(formatMoneyFull(pledge.amount))
                append(" pledged")
                pledge.driveTitle?.let { append(" on $it") }
                append(" hasn't landed.")
            },
        )
    }
}

/** The instant's own UTC calendar date — no `TimeZone` dependency, same reasoning as [dateChip]. */
fun instantToDate(instant: Instant): kotlinx.datetime.LocalDate =
    kotlinx.datetime.LocalDate.parse(instant.toString().substringBefore('T'))
